// src/pages/actividades/components/ActividadDrawer.tsx
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
  Button, Input, Textarea, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Switch, Separator, StatusBadge, PriorityBadge,
} from '@/components/ui';
import {
  Check, RotateCcw, Trash2, Clock, Calendar,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  useActualizarActividad, useCompletarActividad, useReabrirActividad,
  useEliminarActividad, useTiposActividad, useModulos, useSubtareas,
} from '@/hooks/queries/useActividades';
import { useClientes } from '@/hooks/queries/useClientes';
import { useProyectos } from '@/hooks/queries/useProyectos';
import { SubtareaInlineAdd } from './SubtareaInlineAdd';
import { SubtareasList } from './SubtareasList';
import { TiempoPanel } from './TiempoPanel';
import { HistorialTimeline } from './HistorialTimeline';
import { AdjuntosPanel } from '@/components/common';
import type { Actividad, ActividadUpdate, PrioridadActividad, AreaActividad, PatronRecurrencia } from '@/types';

const optionalNumber = z.preprocess(
  (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
  z.number().optional(),
);

const schema = z.object({
  titulo: z.string().min(1, 'El titulo es requerido').max(500),
  descripcion: z.string().optional(),
  tipo_actividad_id: z.string().optional(),
  area: z.enum(['TRABAJO', 'ADMINISTRATIVO', 'PERSONAL']),
  prioridad: z.enum(['AAA', 'AA', 'A', 'B', 'C']),
  cliente_id: z.string().optional(),
  modulo_id: z.string().optional(),
  proyecto_id: z.string().optional(),
  fecha_agendada: z.string().optional(),
  hora_agendada: z.string().optional(),
  fecha_limite: z.string().optional(),
  duracion_estimada_min: optionalNumber,
  es_recurrente: z.boolean().default(false),
  patron_recurrencia: z.string().optional(),
  dias_semana: z.string().optional(),
  dia_mes: z.preprocess(
    (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
    z.number().min(1).max(31).optional(),
  ),
  hora_recurrencia: z.string().optional(),
  fecha_fin_recurrencia: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  actividad: Actividad;
}

const AREAS = [
  { value: 'TRABAJO', label: 'Work' },
  { value: 'ADMINISTRATIVO', label: 'Admin' },
  { value: 'PERSONAL', label: 'Personal' },
];

const PRIORIDADES = [
  { value: 'AAA', label: 'AAA - Maxima' },
  { value: 'AA', label: 'AA - Alta' },
  { value: 'A', label: 'A - Normal' },
  { value: 'B', label: 'B - Baja' },
  { value: 'C', label: 'C - Minima' },
];

const PATRONES = [
  { value: 'DIARIO', label: 'Diario' },
  { value: 'SEMANAL', label: 'Semanal' },
  { value: 'QUINCENAL', label: 'Quincenal' },
  { value: 'MENSUAL', label: 'Mensual' },
];

const DIAS_SEMANA = [
  { value: '1', label: 'L' },
  { value: '2', label: 'M' },
  { value: '3', label: 'Mi' },
  { value: '4', label: 'J' },
  { value: '5', label: 'V' },
  { value: '6', label: 'S' },
  { value: '7', label: 'D' },
];

export function ActividadDrawer({ open, onClose, actividad }: Props) {
  const actualizarMutation = useActualizarActividad();
  const completar = useCompletarActividad();
  const reabrir = useReabrirActividad();
  const eliminar = useEliminarActividad();
  const { data: clientesData } = useClientes({ page: 1, page_size: 100 });
  const { data: tipos } = useTiposActividad();
  const { data: modulos } = useModulos();
  const { data: proyectosData } = useProyectos();
  const { data: subtareas } = useSubtareas(actividad.id);
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    principal: true,
    programacion: true,
    contexto: false,
    subtareas: true,
    adjuntos: false,
    tiempo: false,
    historial: false,
    recurrencia: false,
  });

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      titulo: '',
      area: 'TRABAJO',
      prioridad: 'A',
    },
  });

  const esRecurrente = form.watch('es_recurrente');
  const patronRecurrencia = form.watch('patron_recurrencia');

  useEffect(() => {
    if (actividad && open) {
      form.reset({
        titulo: actividad.titulo,
        descripcion: actividad.descripcion || '',
        tipo_actividad_id: actividad.tipo_actividad_id || '',
        area: actividad.area,
        prioridad: actividad.prioridad,
        cliente_id: actividad.cliente_id || '',
        modulo_id: actividad.modulo_id || '',
        proyecto_id: actividad.proyecto_id || '',
        fecha_agendada: actividad.fecha_agendada || '',
        hora_agendada: actividad.hora_agendada || '',
        fecha_limite: actividad.fecha_limite || '',
        duracion_estimada_min: actividad.duracion_estimada_min,
        es_recurrente: actividad.es_recurrente,
        patron_recurrencia: actividad.patron_recurrencia || '',
        dias_semana: actividad.dias_semana || '',
        dia_mes: actividad.dia_mes,
        hora_recurrencia: actividad.hora_recurrencia || '',
        fecha_fin_recurrencia: actividad.fecha_fin_recurrencia || '',
      });
      // Auto-expand recurrence if active
      if (actividad.es_recurrente) {
        setExpandedSections(prev => ({ ...prev, recurrencia: true }));
      }
    }
  }, [actividad, form, open]);

  const handleSave = async (data: FormData) => {
    // null = clear the field, undefined = don't touch it
    const emptyToNull = (val: string | undefined): string | null | undefined =>
      val === '' ? null : val || undefined;
    const emptyToUndef = (val: string | undefined) => val || undefined;

    const submitData: ActividadUpdate = {
      titulo: data.titulo,
      descripcion: data.descripcion,
      area: data.area,
      prioridad: data.prioridad,
      es_recurrente: data.es_recurrente,
      tipo_actividad_id: emptyToNull(data.tipo_actividad_id),
      cliente_id: emptyToNull(data.cliente_id),
      modulo_id: emptyToNull(data.modulo_id),
      proyecto_id: emptyToNull(data.proyecto_id),
      fecha_agendada: emptyToUndef(data.fecha_agendada),
      hora_agendada: emptyToUndef(data.hora_agendada),
      fecha_limite: emptyToUndef(data.fecha_limite),
      duracion_estimada_min: data.duracion_estimada_min || undefined,
      patron_recurrencia: data.es_recurrente ? emptyToUndef(data.patron_recurrencia) as PatronRecurrencia | undefined : undefined,
      dias_semana: data.es_recurrente ? emptyToUndef(data.dias_semana) : undefined,
      dia_mes: data.es_recurrente ? data.dia_mes : undefined,
      hora_recurrencia: data.es_recurrente ? emptyToUndef(data.hora_recurrencia) : undefined,
      fecha_fin_recurrencia: data.es_recurrente ? emptyToUndef(data.fecha_fin_recurrencia) : undefined,
    };

    try {
      await actualizarMutation.mutateAsync({
        id: actividad.id,
        data: submitData,
      });
      onClose();
    } catch {
      // Hook onError already shows toast
    }
  };

  const handleCompletar = async () => {
    const completarSubs =
      actividad.subtareas_count && actividad.tiene_subtareas_pendientes
        ? window.confirm('Hay subtareas pendientes. ¿Desea completarlas tambien?')
        : false;
    try {
      await completar.mutateAsync({ id: actividad.id, completarSubtareas: completarSubs });
      onClose();
    } catch {
      // Hook onError already shows toast
    }
  };

  const handleReabrir = async () => {
    try {
      await reabrir.mutateAsync(actividad.id);
      onClose();
    } catch {
      // Hook onError already shows toast
    }
  };

  const handleEliminar = async () => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    try {
      await eliminar.mutateAsync(actividad.id);
      onClose();
    } catch {
      // Hook onError already shows toast
    }
  };

  const toggleDiaSemana = (dia: string) => {
    const current = form.watch('dias_semana') || '';
    const dias = current ? current.split(',') : [];
    const idx = dias.indexOf(dia);
    if (idx >= 0) dias.splice(idx, 1);
    else dias.push(dia);
    dias.sort((a, b) => parseInt(a) - parseInt(b));
    form.setValue('dias_semana', dias.join(','), { shouldValidate: true });
  };

  const isCompletable = actividad.estado !== 'COMPLETADA' && actividad.estado !== 'CANCELADA';
  const isLoading = actualizarMutation.isPending;

  // Collapsible section header component
  const SectionHeader = ({ id, title, count }: { id: string; title: string; count?: number }) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
    >
      {expandedSections[id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      {title}
      {count !== undefined && count > 0 && (
        <span className="text-xs text-muted-foreground bg-muted px-1.5 rounded-full">{count}</span>
      )}
    </button>
  );

  const proyectos = proyectosData ?? [];

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
          <SheetHeader>
            <div className="flex items-center gap-2 mb-1">
              <StatusBadge status={actividad.estado} />
              <PriorityBadge priority={actividad.prioridad} />
            </div>
            <SheetTitle className="text-base pr-6">{actividad.titulo}</SheetTitle>
          </SheetHeader>

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3">
            {isCompletable && (
              <Button size="sm" onClick={handleCompletar} loading={completar.isPending}>
                <Check className="h-3.5 w-3.5 mr-1" />
                Completar
              </Button>
            )}
            {actividad.estado === 'COMPLETADA' && (
              <Button size="sm" variant="outline" onClick={handleReabrir} loading={reabrir.isPending}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reabrir
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive ml-auto" onClick={handleEliminar}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Form content */}
        <form onSubmit={form.handleSubmit(handleSave)} className="px-6 py-4 space-y-1">

          {/* ── Principal ─────────────────────────────────────── */}
          <SectionHeader id="principal" title="Principal" />
          {expandedSections.principal && (
            <div className="space-y-3 pb-2">
              <div className="space-y-1.5">
                <Label htmlFor="titulo" className="text-xs">Titulo</Label>
                <Input id="titulo" {...form.register('titulo')} className="h-8 text-sm" />
                {form.formState.errors.titulo && (
                  <p className="text-xs text-red-500">{form.formState.errors.titulo.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Descripcion</Label>
                <Textarea {...form.register('descripcion')} rows={3} className="text-sm" placeholder="..." />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select
                  value={form.watch('tipo_actividad_id') || 'none'}
                  onValueChange={(v) => {
                    const tipoId = v === 'none' ? '' : v;
                    form.setValue('tipo_actividad_id', tipoId);
                    if (tipoId && tipos) {
                      const tipo = tipos.find((t) => t.id === tipoId);
                      if (tipo?.area_default && form.watch('area') === 'TRABAJO') {
                        form.setValue('area', tipo.area_default);
                      }
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Sin tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin tipo</SelectItem>
                    {tipos?.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Area</Label>
                  <Select value={form.watch('area')} onValueChange={(v) => form.setValue('area', v as AreaActividad)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AREAS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Prioridad</Label>
                  <Select value={form.watch('prioridad')} onValueChange={(v) => form.setValue('prioridad', v as PrioridadActividad)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORIDADES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Modulo</Label>
                <Select
                  value={form.watch('modulo_id') || 'none'}
                  onValueChange={(v) => form.setValue('modulo_id', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Sin modulo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin modulo</SelectItem>
                    {modulos && (() => {
                      const grupos = [...new Set(modulos.map((m) => m.grupo))];
                      return grupos.map((grupo) => (
                        <div key={grupo}>
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">{grupo}</div>
                          {modulos.filter((m) => m.grupo === grupo).map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                          ))}
                        </div>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <Separator />

          {/* ── Programacion ──────────────────────────────────── */}
          <SectionHeader id="programacion" title="Programacion" />
          {expandedSections.programacion && (
            <div className="space-y-3 pb-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />Agendada</Label>
                  <Input type="date" {...form.register('fecha_agendada')} className="h-8 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><Clock className="h-3 w-3" />Hora</Label>
                  <Input type="time" {...form.register('hora_agendada')} className="h-8 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Fecha limite</Label>
                  <Input type="date" {...form.register('fecha_limite')} className="h-8 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Duracion (min)</Label>
                  <Input type="number" {...form.register('duracion_estimada_min')} className="h-8 text-sm" placeholder="60" />
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* ── Contexto ──────────────────────────────────────── */}
          <SectionHeader id="contexto" title="Contexto" />
          {expandedSections.contexto && (
            <div className="space-y-3 pb-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Cliente</Label>
                <Select
                  value={form.watch('cliente_id') || 'none'}
                  onValueChange={(v) => form.setValue('cliente_id', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Sin cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {clientesData?.items?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Proyecto</Label>
                <Select
                  value={form.watch('proyecto_id') || 'none'}
                  onValueChange={(v) => form.setValue('proyecto_id', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proyecto</SelectItem>
                    {proyectos.map((p: { id: string; nombre: string }) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <Separator />

          {/* ── Recurrencia ───────────────────────────────────── */}
          <SectionHeader id="recurrencia" title="Recurrencia" />
          {expandedSections.recurrencia && (
            <div className="space-y-3 pb-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Actividad recurrente</Label>
                <Switch
                  checked={esRecurrente}
                  onCheckedChange={(checked) => form.setValue('es_recurrente', checked)}
                />
              </div>

              {esRecurrente && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Patron</Label>
                    <Select
                      value={patronRecurrencia || 'none'}
                      onValueChange={(v) => form.setValue('patron_recurrencia', v === 'none' ? '' : v, { shouldValidate: true })}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccione...</SelectItem>
                        {PATRONES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {patronRecurrencia === 'SEMANAL' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Dias</Label>
                      <div className="flex gap-1">
                        {DIAS_SEMANA.map((dia) => {
                          const selected = (form.watch('dias_semana') || '').split(',').includes(dia.value);
                          return (
                            <button
                              key={dia.value}
                              type="button"
                              onClick={() => toggleDiaSemana(dia.value)}
                              className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                                selected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                              }`}
                            >
                              {dia.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {patronRecurrencia === 'MENSUAL' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Dia del mes</Label>
                      <Select
                        value={form.watch('dia_mes')?.toString() || 'none'}
                        onValueChange={(v) => form.setValue('dia_mes', v === 'none' ? undefined : parseInt(v), { shouldValidate: true })}
                      >
                        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Dia" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seleccione...</SelectItem>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                            <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Hora</Label>
                      <Input type="time" {...form.register('hora_recurrencia')} className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Hasta</Label>
                      <Input type="date" {...form.register('fecha_fin_recurrencia')} className="h-8 text-sm" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <Separator />

          {/* ── Subtareas ─────────────────────────────────────── */}
          <SectionHeader id="subtareas" title="Subtareas" count={subtareas?.length} />
          {expandedSections.subtareas && (
            <div className="pb-2">
              <SubtareasList subtareas={subtareas ?? []} />
              <div className="mt-2">
                <SubtareaInlineAdd actividadPadreId={actividad.id} />
              </div>
            </div>
          )}

          <Separator />

          {/* ── Adjuntos ───────────────────────────────────────── */}
          <SectionHeader id="adjuntos" title="Adjuntos" />
          {expandedSections.adjuntos && (
            <div className="pb-2">
              <AdjuntosPanel contexto="ACTIVIDAD" entidadId={actividad.id} compact />
            </div>
          )}

          <Separator />

          {/* ── Tiempo ────────────────────────────────────────── */}
          <SectionHeader id="tiempo" title="Tiempo" />
          {expandedSections.tiempo && (
            <div className="pb-2">
              <TiempoPanel
                actividadId={actividad.id}
                tiempoRegistradoMin={actividad.tiempo_registrado_min ?? 0}
                compact
              />
            </div>
          )}

          <Separator />

          {/* ── Historial ─────────────────────────────────────── */}
          <SectionHeader id="historial" title="Historial" />
          {expandedSections.historial && (
            <div className="pb-2">
              <HistorialTimeline actividadId={actividad.id} />
            </div>
          )}

          {/* Save button */}
          <div className="sticky bottom-0 bg-background border-t pt-4 pb-2">
            <Button type="submit" loading={isLoading} className="w-full">
              Guardar cambios
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
